/**
 * Character definitions (Phase 2) — the data-driven table.
 *
 * The engine core never hardcodes character names; it only reads this table
 * through the helpers below. Rebalancing = editing the numbers here:
 *   - active.uses            : starting charges of the active ability
 *   - passive.extraWalls     : starting wall bonus
 *   - passive.ignoreGimmicks : immune to all tile gimmicks
 *
 * Active abilities are referenced by id into the generic ABILITIES registry
 * (abilities.js). Adding a character = adding a row here (plus, if it needs
 * a brand-new ability, one registry entry).
 *
 * 建築士 (architect) is deliberately excluded from the initial release
 * (docs/GAME_DESIGN.md §7-3).
 */

import { ABILITIES } from './abilities.js';
import { buildGimmickMap, cellKey } from './gimmicks.js';
import { buildWallSet } from './walls.js';

export const CHARACTERS = {
  none: {
    id: 'none',
    name: 'なし',
    passive: {},
    active: null,
  },
  ninja: {
    id: 'ninja',
    name: '忍者',
    passive: {},
    active: { ability: 'wallJump', uses: 1 },
  },
  mage: {
    id: 'mage',
    name: '魔術師',
    passive: {},
    active: { ability: 'removeWall', uses: 1 },
  },
  heavyweight: {
    id: 'heavyweight',
    name: '重戦士',
    passive: { extraWalls: 2 },
    active: null,
  },
  explorer: {
    id: 'explorer',
    name: '探検家',
    passive: { ignoreGimmicks: true },
    active: { ability: 'diagonalStep', uses: 1 },
  },
  // --- Expansion pool (spec: widen first, rebalance later). All are
  // active-1 / passive-none; rules are deterministic, balance is TBD. ---
  sprinter: {
    id: 'sprinter',
    name: '韋駄天',
    passive: {},
    active: { ability: 'dash', uses: 1 },
  },
  phantom: {
    id: 'phantom',
    name: '幻術師',
    passive: {},
    active: { ability: 'swap', uses: 1 },
  },
  brute: {
    id: 'brute',
    name: '怪力',
    passive: {},
    active: { ability: 'shove', uses: 2 },
  },
  hexer: {
    id: 'hexer',
    name: '呪術師',
    passive: {},
    active: { ability: 'hex', uses: 1 },
  },
  thief: {
    id: 'thief',
    name: '盗賊',
    passive: {},
    active: { ability: 'steal', uses: 1 },
  },
};

/** Definition for a character id; unknown ids behave like 'none'. */
export function characterDef(id) {
  return CHARACTERS[id] ?? CHARACTERS.none;
}

/** Does this player's character ignore all tile gimmicks? (explorer passive) */
export function ignoresGimmicks(state, playerIndex) {
  return (
    characterDef(state.players[playerIndex].character).passive.ignoreGimmicks ===
    true
  );
}

/**
 * Shared context for move/ability candidate generation.
 * gimmickMap is null when there are no gimmicks or the player ignores them,
 * so classic mode pays no gimmick cost.
 */
export function moveContextFor(state, playerIndex) {
  const boardSize = state.config.boardSize;
  const wallSet = buildWallSet(state.walls);
  const occupied = new Set();
  for (let i = 0; i < state.players.length; i++) {
    if (i === playerIndex) continue;
    const p = state.players[i].pawn;
    occupied.add(cellKey(p.col, p.row));
  }
  const gimmicks = state.config.gimmicks;
  const gimmickMap =
    gimmicks && gimmicks.length > 0 && !ignoresGimmicks(state, playerIndex)
      ? buildGimmickMap(gimmicks)
      : null;
  return { boardSize, wallSet, occupied, gimmickMap };
}

/**
 * Character-aware context for the path-existence BFS (wall legality,
 * pathExists, shortestPathLength). Spec §8-8:
 * - ninja: remaining wallJump uses count as wall crossings — reachability
 *   is computed over states (cell, jumpsLeft), so enclosing the ninja is
 *   legal while a jump remains and illegal once it is spent.
 * - ice slides are part of the movement graph and are folded into the BFS.
 * - swamp only delays, never disconnects — invisible to the BFS.
 * - mage removeWall / explorer diagonalStep are intentionally NOT part of
 *   the reachability guarantee (see abilities.js for the rationale).
 *
 * Returns null for the classic case (no jumps, no applicable gimmicks) so
 * the BFS keeps its fast Phase 1 code path.
 */
export function pathContextFor(state, playerIndex) {
  const player = state.players[playerIndex];
  let wallJumps = 0;
  if (player.ability && player.ability.usesLeft > 0) {
    const def = ABILITIES[player.ability.id];
    if (def && def.pathEffect === 'wallCrossing') {
      wallJumps = player.ability.usesLeft;
    }
  }
  const gimmicks = state.config.gimmicks;
  const useGimmicks = !!(
    gimmicks &&
    gimmicks.length > 0 &&
    !ignoresGimmicks(state, playerIndex)
  );
  if (wallJumps === 0 && !useGimmicks) return null;
  return {
    wallJumps,
    gimmickMap: useGimmicks ? buildGimmickMap(gimmicks) : null,
  };
}
